import React from "react";
import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";

const DATE_RANGES = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "all", label: "All" },
];

const SectionLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    color="text.secondary"
    sx={{ letterSpacing: "0.08em", mb: 0.75 }}
  >
    {children}
  </Typography>
);

const FiltersPanel = ({
  sources,
  onSourceToggle,
  search,
  onSearchChange,
  dateRange,
  onDateRangeChange,
  confidenceMin,
  onConfidenceChange,
  onReset,
}) => {
  return (
    <Card
      sx={{
        p: 2.5,
        position: "sticky",
        top: 16,
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6),
      }}
      elevation={0}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Filters</Typography>
        <IconButton size="small" onClick={onReset} title="Reset all filters">
          <RestartAltIcon fontSize="small" />
        </IconButton>
      </Box>

      <SectionLabel>Search</SectionLabel>
      <TextField
        size="small"
        fullWidth
        placeholder="Title, content, source…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
      />

      {sources && sources.length > 0 && (
        <>
          <Divider sx={{ my: 2.5 }} />

          <SectionLabel>Sources</SectionLabel>
          <Stack spacing={0.25} sx={{ maxHeight: 500, overflowY: "scroll" }}>
            {sources.map((s) => (
              <FormControlLabel
                key={s.key}
                control={
                  <Checkbox
                    size="small"
                    checked={s.checked}
                    onChange={() => onSourceToggle(s.key)}
                    sx={{ color: s.color, "&.Mui-checked": { color: s.color } }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">{s.label}</Typography>
                    <Typography variant="caption" color="text.disabled">
                      {typeof s.count === "number" ? s.count.toLocaleString() : ""}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Stack>
        </>
      )}

      <Divider sx={{ my: 2.5 }} />

      <SectionLabel>Date range</SectionLabel>
      <ToggleButtonGroup
        size="small"
        value={dateRange}
        exclusive
        onChange={(_, v) => v && onDateRangeChange(v)}
        sx={{ display: "flex", "& .MuiToggleButton-root": { flex: 1, fontSize: "0.75rem", py: 0.5 } }}
      >
        {DATE_RANGES.map((r) => (
          <ToggleButton key={r.value} value={r.value}>
            {r.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Divider sx={{ my: 2.5 }} />

      <SectionLabel>Min confidence</SectionLabel>
      <Box sx={{ px: 1 }}>
        <Slider
          size="small"
          value={confidenceMin}
          onChange={(_, v) => onConfidenceChange(v)}
          min={0}
          max={1}
          step={0.05}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${Math.round(v * 100)}%`}
          marks={[
            { value: 0, label: "0%" },
            { value: 0.5, label: "50%" },
            { value: 1, label: "100%" },
          ]}
        />
      </Box>
    </Card>
  );
};

export default FiltersPanel;
